// data-fit LLC · DecisionFlow · ADMIN Stage
// AuditLogViewer.jsx · DDL: R5 · API: v1.11 · Session 94
// Auth: useAuth() direct — ARCH-2 compliant
// Key constraints: read-only audit screen; blank filters omitted; fiscal_period_id integer query only
// Open Items: OI-ADMIN-AUDIT-LOG-LIST
// M040 C2: dark: prefix removed, hardcoded colors → tokens, text-* → text-tok-*

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../auth/AuthContext.jsx'
import { SCREEN_ORDER } from './AdminFlowShell'
import { ChevronUp, ChevronDown, ChevronsUpDown, Search, X } from 'lucide-react'

const INITIAL_FILTERS = {
  entity_type: '',
  event_type: '',
  fiscal_period_id: '',
}

const truncateId = (value) => {
  if (!value) return '—'
  const stringValue = String(value)
  return stringValue.length > 12 ? `${stringValue.slice(0, 8)}…` : stringValue
}

const formatJsonState = (value) => {
  if (value === null || value === undefined) return '—'
  return JSON.stringify(value, null, 2)
}

const getErrorEnvelope = (err) => {
  if (err?.error) {
    const { code, message } = err.error
    return { code, message }
  }

  return {
    code: 'UNKNOWN_ERROR',
    message: 'An unexpected error occurred.',
  }
}

const parseApiResponse = async (response) => {
  const payload = await response.json().catch(() => ({}))

  if (!response.ok) {
    throw payload?.error
      ? payload
      : {
          error: {
            code: 'HTTP_ERROR',
            http_status: response.status,
            message: 'Request failed.',
          },
        }
  }

  return payload
}

const getRowsFromEnvelope = (payload) => {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.items)) return payload.items
  if (Array.isArray(payload?.results)) return payload.results
  return []
}

const getPaginationFromEnvelope = (payload) => ({
  next_cursor: payload?.pagination?.next_cursor || null,
  has_more: Boolean(payload?.pagination?.has_more),
})

export default function AuditLogViewer({
  aiStatus,
  aiResponse,
  currentScreen,
  onNavigate,
}) {
  const { token } = useAuth()

  const [auditLogs, setAuditLogs] = useState([])
  const [filters, setFilters] = useState(INITIAL_FILTERS)
  // expandedAuditLogId/handleToggleExpanded replaced by selectedLog side panel (S579)

  const [nextCursor, setNextCursor] = useState(null)
  const [hasMore, setHasMore] = useState(false)

  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)

  const buildQueryParams = ({ cursor = null, activeFilters = filters } = {}) => {
    const queryParams = new URLSearchParams()

    if (activeFilters.entity_type !== '') {
      queryParams.set('entity_type', activeFilters.entity_type)
    }

    if (activeFilters.event_type !== '') {
      queryParams.set('event_type', activeFilters.event_type)
    }

    if (
      activeFilters.fiscal_period_id !== '' &&
      !Number.isNaN(Number(activeFilters.fiscal_period_id))
    ) {
      queryParams.set('fiscal_period_id', String(Number(activeFilters.fiscal_period_id)))
    }

    if (cursor) {
      queryParams.set('cursor', cursor)
    }

    return queryParams
  }

  const loadAuditLogs = useCallback(
    async ({ cursor = null, append = false, activeFilters = filters } = {}) => {
      if (append) {
        setIsLoadingMore(true)
      } else {
        setIsLoading(true)
      }

      setErrorMessage(null)

      try {
        const queryParams = buildQueryParams({ cursor, activeFilters })
        const queryString = queryParams.toString()
        const url = queryString ? `/api/v1/audit-logs?${queryString}` : '/api/v1/audit-logs'

        // OI-ADMIN-AUDIT-LOG-LIST: /api/v1/audit-logs unconfirmed in v1.11 — stub pending v1.12
        const response = await fetch(url, {
          method: 'GET',
          credentials: 'include',
        })

        const payload = await parseApiResponse(response)
        const rows = getRowsFromEnvelope(payload)
        const pagination = getPaginationFromEnvelope(payload)

        if (append) {
          setAuditLogs((currentRows) => [...currentRows, ...rows])
        } else {
          setAuditLogs(rows)
        }

        setNextCursor(pagination.next_cursor)
        setHasMore(pagination.has_more)
      } catch (err) {
        const { code, message } = getErrorEnvelope(err)
        setErrorMessage(message || `Unable to load audit logs. (${code})`)
      } finally {
        setIsLoading(false)
        setIsLoadingMore(false)
      }
    },
    [token]
  )

  useEffect(() => {
    loadAuditLogs({ activeFilters: INITIAL_FILTERS })
  }, [loadAuditLogs])

  const handleApplyFilters = () => {
    setAuditLogs([])
    setNextCursor(null)
    setHasMore(false)
    loadAuditLogs({ append: false, activeFilters: filters })
  }

  const handleClearFilters = () => {
    setFilters({ entity_type: '', event_type: '', fiscal_period_id: '' })
    setAuditLogs([])
    setNextCursor(null)
    setHasMore(false)
    loadAuditLogs({ append: false, activeFilters: INITIAL_FILTERS })
  }

  // renderRows replaced by inline sort/search render + side panel (S579)

  // S579 — OI-UI-TABLE-CONSISTENCY
  const [search, setSearch]   = useState('')
  const [sortCol, setSortCol] = useState('created_at')
  const [sortDir, setSortDir] = useState('desc')

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    else { setSortCol(col); setSortDir('asc') }
  }

  const SortIcon = ({ col }) => {
    if (sortCol !== col) return <ChevronsUpDown className="inline h-3 w-3 ml-0.5 text-[rgb(var(--color-text-muted))]" />
    return sortDir === 'asc'
      ? <ChevronUp className="inline h-3 w-3 ml-0.5 text-[rgb(var(--color-primary))]" />
      : <ChevronDown className="inline h-3 w-3 ml-0.5 text-[rgb(var(--color-primary))]" />
  }

  const filtered = useMemo(() => {
    const q = search.toLowerCase()
    return auditLogs.filter((r) =>
      !q ||
      (r.event_type ?? '').toLowerCase().includes(q) ||
      (r.entity_type ?? '').toLowerCase().includes(q) ||
      (r.change_summary ?? '').toLowerCase().includes(q)
    )
  }, [auditLogs, search])

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let av = '', bv = ''
      if (sortCol === 'created_at')       { av = a.created_at ?? '';     bv = b.created_at ?? '' }
      else if (sortCol === 'event_type')  { av = a.event_type ?? '';     bv = b.event_type ?? '' }
      else if (sortCol === 'entity_type') { av = a.entity_type ?? '';    bv = b.entity_type ?? '' }
      else if (sortCol === 'fiscal_period_id') { av = a.fiscal_period_id ?? 0; bv = b.fiscal_period_id ?? 0 }
      else if (sortCol === 'change_summary')   { av = a.change_summary ?? ''; bv = b.change_summary ?? '' }
      const cmp = av < bv ? -1 : av > bv ? 1 : 0
      return sortDir === 'asc' ? cmp : -cmp
    })
  }, [filtered, sortCol, sortDir])

  const thCls = 'px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] cursor-pointer select-none whitespace-nowrap hover:text-[rgb(var(--color-text))]'
  const tdCls = 'px-3 py-2.5 text-tok-sm text-[rgb(var(--color-text))] whitespace-nowrap'

  // selected row for side panel — replaces inline expand
  const [selectedLog, setSelectedLog] = useState(null)

  return (
    <div className="flex flex-col gap-4">
      <div className="mb-1">
        <h2 className="text-tok-base font-bold text-[rgb(var(--color-text))]">Audit Log Viewer</h2>
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))] mt-0.5">
          Review append-only audit history. This screen is read-only and exposes no mutation controls.
        </p>
      </div>

      {/* Filters — unchanged */}
      <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-4 flex flex-col gap-3">
        <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Filters</h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label className="flex flex-col gap-1 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
            Entity Type
            <input type="text" value={filters.entity_type} onChange={(event) => setFilters((current) => ({ ...current, entity_type: event.target.value }))} className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))]" />
          </label>
          <label className="flex flex-col gap-1 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
            Event Type
            <input type="text" value={filters.event_type} onChange={(event) => setFilters((current) => ({ ...current, event_type: event.target.value }))} className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))]" />
          </label>
          <label className="flex flex-col gap-1 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
            Fiscal Period ID
            <input type="number" step="1" value={filters.fiscal_period_id} onChange={(event) => setFilters((current) => ({ ...current, fiscal_period_id: event.target.value }))} className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))]" />
          </label>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button type="button" className="rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-xs font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50" onClick={handleApplyFilters} disabled={isLoading}>Apply Filters</button>
          <button type="button" className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50" onClick={handleClearFilters} disabled={isLoading}>Clear Filters</button>
        </div>
      </div>

      {errorMessage && <div className="rounded-lg bg-[rgb(var(--color-danger))]/10 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{errorMessage}</div>}

      {/* Audit Logs table */}
      <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-4 flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Audit Logs {auditLogs.length > 0 && <span className="ml-1 text-tok-xs font-normal text-[rgb(var(--color-text-muted))]">({auditLogs.length})</span>}</h3>
          <div className="relative max-w-xs flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />
            <input
              type="text"
              placeholder="Search logs…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] py-1.5 pl-8 pr-3 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none"
            />
          </div>
          {search && <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">{sorted.length} of {auditLogs.length}</span>}
        </div>

        <div className="overflow-auto max-h-[calc(100vh-22rem)] rounded-xl border border-[rgb(var(--color-border))]">
          <table className="w-full text-tok-sm">
            <thead className="sticky top-0 bg-[rgb(var(--color-surface-muted))] z-10">
              <tr>
                {[
                  ['created_at','Created At'],
                  ['event_type','Event Type'],
                  ['entity_type','Entity Type'],
                  ['entity_id','Entity ID'],
                  ['fiscal_period_id','Fiscal Period'],
                  ['user_id','User ID'],
                  ['change_summary','Change Summary'],
                ].map(([col, label]) => (
                  <th key={col} className={thCls} onClick={() => toggleSort(col)}>
                    {label}<SortIcon col={col} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan="7" className="px-3 py-4 text-tok-sm text-[rgb(var(--color-text-muted))]">Loading audit logs…</td></tr>}
              {!isLoading && sorted.length === 0 && (
                <tr><td colSpan="7" className="px-3 py-4 text-tok-sm text-[rgb(var(--color-text-muted))]">{search ? 'No logs match your search.' : 'No audit logs found.'}</td></tr>
              )}
              {!isLoading && sorted.map((row) => (
                <tr
                  key={row.audit_log_id}
                  className={`border-t border-[rgb(var(--color-border))] cursor-pointer transition-colors hover:bg-[rgb(var(--color-surface-muted))] ${selectedLog?.audit_log_id === row.audit_log_id ? 'bg-[rgb(var(--color-surface-muted))]' : ''}`}
                  onClick={() => setSelectedLog(selectedLog?.audit_log_id === row.audit_log_id ? null : row)}
                >
                  <td className={tdCls}>{row.created_at}</td>
                  <td className={tdCls}>{row.event_type}</td>
                  <td className={tdCls}>{row.entity_type}</td>
                  <td className={`${tdCls} font-mono text-tok-xs`} title={row.entity_id || ''}>{truncateId(row.entity_id)}</td>
                  <td className={tdCls}>{row.fiscal_period_id ?? '—'}</td>
                  <td className={`${tdCls} font-mono text-tok-xs`} title={row.user_id || ''}>{truncateId(row.user_id)}</td>
                  <td className={tdCls}>{row.change_summary || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {hasMore && (
          <button type="button" className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50 self-start" onClick={() => loadAuditLogs({ cursor: nextCursor, append: true, activeFilters: filters })} disabled={isLoadingMore}>
            {isLoadingMore ? 'Loading more…' : 'Load More'}
          </button>
        )}
      </div>

      {/* Detail side panel — replaces inline expand rows */}
      {selectedLog && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setSelectedLog(null)} />
          <div className="fixed top-0 right-0 z-40 h-full w-96 bg-[rgb(var(--color-surface))] border-l border-[rgb(var(--color-border))] shadow-xl flex flex-col">
            <div className="flex items-start justify-between border-b border-[rgb(var(--color-border))] px-5 py-4 shrink-0">
              <div>
                <p className="text-tok-xs font-medium uppercase tracking-wide text-[rgb(var(--color-text-muted))]">Audit Log Detail</p>
                <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">{selectedLog.event_type}</p>
              </div>
              <button onClick={() => setSelectedLog(null)} className="rounded-lg p-1 hover:bg-[rgb(var(--color-surface-muted))]"><X className="h-4 w-4" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                {[
                  ['Audit Log ID', selectedLog.audit_log_id],
                  ['Tenant ID', selectedLog.tenant_id],
                  ['User ID', selectedLog.user_id || '—'],
                  ['Session ID', selectedLog.session_id || '—'],
                  ['Event Type', selectedLog.event_type],
                  ['Entity Type', selectedLog.entity_type],
                  ['Entity ID', selectedLog.entity_id || '—'],
                  ['Fiscal Period ID', selectedLog.fiscal_period_id ?? '—'],
                  ['Change Summary', selectedLog.change_summary || '—'],
                  ['IP Address', selectedLog.ip_address || '—'],
                  ['User Agent', selectedLog.user_agent || '—'],
                  ['Created At', selectedLog.created_at],
                  ['Request ID', selectedLog.request_id || '—'],
                ].map(([label, value]) => (
                  <div key={label} className="flex flex-col gap-0.5">
                    <span className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">{label}</span>
                    <span className="text-tok-sm text-[rgb(var(--color-text))] break-all">{value}</span>
                  </div>
                ))}
              </div>
              <div>
                <h4 className="text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] mb-1">Before State</h4>
                <pre className="text-tok-xs bg-[rgb(var(--color-surface-muted))] border border-[rgb(var(--color-border))] rounded-lg p-2 overflow-x-auto">{formatJsonState(selectedLog.before_state)}</pre>
              </div>
              <div>
                <h4 className="text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] mb-1">After State</h4>
                <pre className="text-tok-xs bg-[rgb(var(--color-surface-muted))] border border-[rgb(var(--color-border))] rounded-lg p-2 overflow-x-auto">{formatJsonState(selectedLog.after_state)}</pre>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
